import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";
import { getEnv } from "@src/utils/env-utils";
import { GATSBY_ENV } from "@src/utils/constants";
import { formatDatadogVersion } from "./formatDatadogVersion";

const DATADOG_RUM_APPLICATION_ID = "341daedf-9cd8-4e41-8f1c-890107686f00";
const DATADOG_RUM_CLIENT_TOKEN = "pub52326dd40ea6e1dfc79abf14954a0bfb";
const DATADOG_SITE = "datadoghq.eu";

type Stage = "prod" | "preprod" | "dev";

/**
 * Déduit le stage à partir de GATSBY_ENV.
 * L'ordre est important : "preprod" doit être testé avant "prod"
 * car la chaîne "preprod" contient "prod".
 */
const getStageFromEnv = (env: string): Stage => {
  if (env.includes("preprod")) return "preprod";
  if (env.includes("prod")) return "prod";
  return "dev";
};

let initialized = false;

/**
 * GATSBY_RELEASE_VERSION (pipeline Azure DevOps) a le format
 * "<timestamp_build>_<semver>" (ex: "20260929T155439_1.42.0", ou
 * "20260929T155439_1.42.0_test" si le semver lui-même contient un "_").
 * On retire uniquement le préfixe timestamp (`<digits>T<digits>_`) plutôt que
 * de couper sur le dernier "_" (qui tronquerait un semver contenant un "_").
 */
const TIMESTAMP_PREFIX_REGEXP = /^\d+T\d+_?/;
const extractSemverFromReleaseVersion = (releaseVersion: string): string =>
  releaseVersion.replace(TIMESTAMP_PREFIX_REGEXP, "") || releaseVersion;

/**
 * Initialise le RUM (Real User Monitoring) DataDog côté client.
 * - Ne fait rien côté SSR (le SDK accède à document/window).
 * - Idempotent : ne s'initialise qu'une seule fois.
 * - Pas de router React (routing géré par Gatsby, pas react-router).
 */
export const initDatadogRum = (): void => {
  if (typeof window === "undefined" || initialized) return;

  const env = getEnv("GATSBY_ENV", "dev");
  const stage = getStageFromEnv(env);
  const releaseVersion = getEnv("GATSBY_RELEASE_VERSION", "unknown");
  const version = extractSemverFromReleaseVersion(releaseVersion);

  datadogRum.init({
    applicationId: DATADOG_RUM_APPLICATION_ID,
    clientToken: DATADOG_RUM_CLIENT_TOKEN,
    site: DATADOG_SITE,
    service: GATSBY_ENV,
    env: stage,
    version: formatDatadogVersion(`${releaseVersion}`),
    sessionSampleRate: 100,
    plugins: [reactPlugin({ router: false })],
    trackViewsManually: true,
  });

  // Contexte global (pas un tag indexé) : porte le semver seul (sans
  // horodatage) sur tous les évènements RUM (dont les erreurs), pour agréger
  // les builds d'une même version, le tag `version` identifiant le build.
  datadogRum.setGlobalContextProperty("rs_version", version);

  initialized = true;
};
